// P1-9 (Agent B): Master-only "default tenant" repair surface.
//
// Surfaces:
//   - site mode (informational)
//   - current default tenant id + health (`ok | missing | inactive | unset`)
//   - repin form (select a new default tenant)
//
// Calls `POST /api/v1/admin/site/default-tenant` (Agent A is wiring this
// up in a parallel lane). If the endpoint isn't live yet, the form
// surfaces the resulting 404 cleanly with operator-actionable copy and
// the page still loads.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  defaultTenantHealthLabel,
  siteModeLabel,
  statusLabel,
} from '../lib/humanLabels.js';

interface TenantSummary {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

const HEALTH_TONE: Record<string, string> = {
  ok: 'bg-secondary-container text-on-secondary-container',
  missing: 'bg-error-container text-on-error-container',
  inactive: 'bg-tertiary-fixed text-on-tertiary-fixed',
  unset: 'bg-surface-container-high text-on-surface-variant',
};

const HEALTH_DETAIL: Record<string, string> = {
  ok: 'Pinned default tenant exists and is active. No action required.',
  missing:
    'The pinned default tenant no longer exists in the database. Pick a new one below to restore tenant-scoped pages.',
  inactive:
    'The pinned default tenant is suspended or archived. Reactivate it (or pick a different one) to restore tenant-scoped pages.',
  unset:
    'No default tenant is pinned. The API silently falls back to "first active tenant" — pin one explicitly to avoid surprises.',
};

export function DefaultTenantSettingsPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<string>('');
  const [toast, setToast] = useState<string | null>(null);

  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';

  useEffect(() => {
    if (state.status !== 'authed') return;
    if (!isMaster) {
      navigate('/', { replace: true });
    }
  }, [state, isMaster, navigate]);

  const reload = useCallback(() => {
    setTenants(null);
    setError(null);
    apiClient
      .get<{ data: TenantSummary[] }>('/api/v1/tenants', { skipTenantScope: true })
      .then((res) => setTenants(res.data))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Failed to load tenants'),
      );
  }, []);

  useEffect(() => {
    if (isMaster) reload();
  }, [isMaster, reload]);

  if (state.status !== 'authed' || !isMaster) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const me = state.me;
  const health = me.default_tenant_health;
  const currentDefaultId = me.default_tenant_id;
  const currentTenant = tenants?.find((t) => t.id === currentDefaultId);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(
        '/api/v1/admin/site/default-tenant',
        { tenant_id: selected },
        { skipTenantScope: true },
      );
      setToast(
        'Default tenant pinned. Reload /auth/me to see the new value on this session.',
      );
      setSelected('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(
          'The backend endpoint /admin/site/default-tenant is not available yet. Coordinate with the API lane to land it.',
        );
      } else {
        setError(err instanceof ApiError ? err.message : 'Repin failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="flex items-center gap-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
          <Icon name="domain" className="text-2xl" />
        </div>
        <div>
          <h1 className="font-headline-md text-headline-md text-on-surface">
            Default tenant
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Pinned site default. Drives tenant-scoped pages on single-tenant
            and multi-family installs. Master admins only.
          </p>
        </div>
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md"
        >
          {toast}
        </div>
      ) : null}

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Definition label="Site mode" value={siteModeLabel(me.site_mode)} />
          <Definition
            label="Current default tenant"
            value={
              currentTenant
                ? `${currentTenant.name} (${statusLabel(currentTenant.status)})`
                : currentDefaultId
                  ? `${currentDefaultId.slice(0, 8)}…`
                  : '— none pinned —'
            }
          />
          <Definition
            label="Health"
            value={
              <span
                // eslint-disable-next-line security/detect-object-injection -- health is a fixed Zod enum
                className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${HEALTH_TONE[health] ?? ''}`}
              >
                {defaultTenantHealthLabel(health)}
              </span>
            }
          />
        </div>
        <p className="font-body-md text-body-md text-on-surface">
          {/* eslint-disable-next-line security/detect-object-injection -- health is a fixed Zod enum */}
          {HEALTH_DETAIL[health] ?? ''}
        </p>
      </section>

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-4">
        <h2 className="font-headline-sm text-headline-sm text-on-surface">
          Repin default tenant
        </h2>
        {error ? (
          <div className="text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : null}
        {tenants === null ? (
          <div className="p-6 flex justify-center">
            <Spinner />
          </div>
        ) : tenants.filter((t) => t.status === 'active').length === 0 ? (
          <p className="font-body-md text-body-md text-on-surface-variant">
            No active tenants. Create or reactivate one on the{' '}
            <Link to="/tenants" className="underline">
              Tenants
            </Link>{' '}
            page first.
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <label
              htmlFor="default-tenant-select"
              className="font-label-md text-label-md text-on-surface block"
            >
              Select an active tenant
            </label>
            <select
              id="default-tenant-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              required
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none"
            >
              <option value="">— pick one —</option>
              {tenants
                .filter((t) => t.status === 'active')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.id === currentDefaultId ? ' (current)' : ''}
                  </option>
                ))}
            </select>
            <div className="flex justify-end gap-2">
              <Button type="submit" loading={submitting} disabled={!selected}>
                Pin as default
              </Button>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant">
              TODO(agent-a): the backend endpoint{' '}
              <code>POST /admin/site/default-tenant</code> needs to land in
              the API lane. This UI calls it but tolerates a 404 cleanly.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}

function Definition({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="font-label-md text-label-md text-on-surface-variant uppercase">
        {label}
      </div>
      <div className="mt-1 font-body-md text-body-md text-on-surface">{value}</div>
    </div>
  );
}
